import { useEffect, useRef } from "react";

/**
 * useCallerInPeer — minimal 1:1 WebRTC voice bridge.
 *
 * Two roles, audio direction depends on whether the host is in "private
 * mode" (talk-back) or just listening:
 *
 *   - role="caller"   ALWAYS sends mic (`localStream`). Receives host
 *                     audio whenever the host has supplied a `localStream`
 *                     (i.e. host is talking back privately).
 *   - role="host"     Always receives caller audio. Sends mic only when
 *                     `localStream` is provided (private-mode talk-back).
 *
 * Signaling is via WebSocket at /api/ws/caller-in/{stationSlug}/{role}
 * The server is a dumb relay; both sides own peer-connection state.
 */
export default function useCallerInPeer({
  enabled,
  role,                 // "caller" | "host"
  stationSlug,
  token,
  localStream = null,   // caller: required (mic). host: optional (private-mode mic)
  onRemoteStream,       // called with the peer's incoming MediaStream
  onConnected,          // optional
  onClosed,             // optional
  apiBase,
}) {
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  // Track whether we've already initiated SDP — caller initiates once
  // the host signals presence. Without this guard a reconnect could
  // double-offer and confuse the peer.
  const initiatedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !stationSlug || !token || !apiBase) return;
    if (role === "caller" && !localStream) return;

    let closed = false;
    let pc = null;

    const wsUrl = apiBase.replace(/^http/, "ws") +
      `/api/ws/caller-in/${encodeURIComponent(stationSlug)}/${role}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    const ensurePc = (iceServers) => {
      if (pc) return pc;
      pc = new RTCPeerConnection({ iceServers: iceServers || [{ urls: "stun:stun.l.google.com:19302" }] });
      pcRef.current = pc;
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
          onConnected?.();
        }
      };
      pc.onicecandidate = (e) => {
        if (e.candidate && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ice", payload: e.candidate.toJSON() }));
        }
      };
      // Both roles want to *receive* audio from the peer. The common
      // ontrack handler routes incoming MediaStreams to the parent.
      pc.ontrack = (e) => {
        if (e.streams && e.streams[0]) onRemoteStream?.(e.streams[0]);
      };
      // Send our local audio if we have one. Caller always; host only
      // when private-mode mic is supplied.
      if (localStream) {
        const audio = localStream.getAudioTracks?.()[0];
        if (audio) pc.addTrack(audio, localStream);
      }
      // For the host (no local stream), explicitly request to receive
      // audio so the offer/answer SDP includes a recvonly m-line.
      if (role === "host" && !localStream) {
        try { pc.addTransceiver("audio", { direction: "recvonly" }); } catch {}
      }
      return pc;
    };

    const callerInitiate = async (iceServers) => {
      if (initiatedRef.current) return;
      initiatedRef.current = true;
      const _pc = ensurePc(iceServers);
      // Caller wants both directions — host may send mic during private mode.
      const offer = await _pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
      await _pc.setLocalDescription(offer);
      ws.send(JSON.stringify({ type: "offer", payload: offer }));
    };

    let savedIceServers = null;

    ws.onmessage = async (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      const { type, payload } = msg;
      if (type === "welcome") {
        savedIceServers = payload?.ice_servers;
        // Caller initiates as soon as host is already there.
        if (role === "caller" && payload?.peer_present) callerInitiate(savedIceServers);
      } else if (type === "peer_present" && role === "caller") {
        callerInitiate(savedIceServers);
      } else if (type === "offer" && role === "host") {
        const _pc = ensurePc(savedIceServers);
        await _pc.setRemoteDescription(new RTCSessionDescription(payload));
        const answer = await _pc.createAnswer();
        await _pc.setLocalDescription(answer);
        ws.send(JSON.stringify({ type: "answer", payload: answer }));
      } else if (type === "answer" && role === "caller") {
        if (pc) await pc.setRemoteDescription(new RTCSessionDescription(payload));
      } else if (type === "ice") {
        if (pc && payload) {
          try { await pc.addIceCandidate(new RTCIceCandidate(payload)); } catch { /* ignore */ }
        }
      } else if (type === "peer_left" || type === "bye") {
        // The other side hung up. Tear down the PC but keep the WS so
        // a re-promote can re-establish without a fresh socket.
        try { pc?.close(); } catch {}
        pc = null;
        pcRef.current = null;
        initiatedRef.current = false;
        onClosed?.();
      }
    };

    ws.onclose = () => {
      if (closed) return;
      closed = true;
      try { pc?.close(); } catch {}
      onClosed?.();
    };

    return () => {
      closed = true;
      try { ws.send(JSON.stringify({ type: "bye" })); } catch {}
      try { ws.close(); } catch {}
      try { pc?.close(); } catch {}
      pcRef.current = null;
      wsRef.current = null;
      initiatedRef.current = false;
    };
  // We intentionally exclude callbacks from deps — they're stable from parent
  // useCallback or unstable but harmless. Re-running the connection on every
  // render would tear down the call.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, role, stationSlug, token, localStream, apiBase]);
}
